// ════════════════════════════════════════════════════════════
//  roster.js — THE TWELVE LINES, read off the canon cards
// ════════════════════════════════════════════════════════════
//  Every stat here is transcribed from the card itself
//  (LOCAL AI STACK/Card Forge/card-texts.json, with the label
//  fixes in CARD-FIXES.md applied: Lyf is always the LEFT number,
//  Lyt the RIGHT). cards.js is NOT the source. If a number here
//  disagrees with a printed card, the card wins and this file is
//  the bug.
//
//  Corner words follow the 2026-10-11 ruling: KIN (Beast /
//  Humanoid / Machine / Dragon) and ILK (Astraea / Phantom / Time /
//  Dragon). Lord names: gamemaster/canon/I_UNIVERSE.md §1.6.
//
//  ── SHATTERPROOF (caP'n, 2026-10-15) ────────────────────────
//  A being fights with its CARD numbers, as they are printed:
//    Lyt    = the power of its Lyt Attack.
//    Lyf    = the shield over its life. Worn down by hits, it mends
//             1 per second once 5 seconds pass without damage.
//    Anima  = its life underneath: 20 Beb · 30 Champion · 40 Lord.
//             Exposed only while Lyf is down (or by an Art that says
//             so). Only Arts restore it. Zero = ruined.
//  Some lines traded their Lyf for Lyt (Chaos Dragon, Z4Z Demon,
//  Xulan Shadow, H-09 Hyper Nova…): no shield, Anima always exposed.
// ════════════════════════════════════════════════════════════

export const TIER_ORDER = ["beb", "champion", "lord"];
export const TIER_LABEL = { beb: "Beb", champion: "Champion", lord: "Lord" };
export const TIER_LEVEL = { beb: 1, champion: 2, lord: 3 };
export const ANIMA_BY_TIER = { beb: 20, champion: 30, lord: 40 };

// How each Kin carries itself. speed = units/s, accel = how fast it
// gets there (fluidity lives here), mass = how far hits shove it.
export const KIN_MOTION = {
  Beast:    { speed: 6.6, accel: 26, mass: 1.0 },
  Dragon:   { speed: 5.4, accel: 18, mass: 1.35 },
  Machine:  { speed: 4.9, accel: 15, mass: 1.6 },
  Humanoid: { speed: 5.6, accel: 20, mass: 1.1 },
};
export const TIER_SCALE = { beb: 1.0, champion: 1.12, lord: 1.25 };

// THE PRIMERA — every being's dodge, set by its Ilk (caP'n 2026-10-15)
export const PRIMERA = {
  Phantom: { name: "Phase",  note: "Stay where you are — Intangible for 1 second. You can still move." },
  Astraea: { name: "Mark",   note: "Mark a spot within reach; 1 second later you are there." },
  Time:    { name: "Rewind", note: "Step back to where you stood 1 second ago." },
  Dragon:  { name: "Leap",   note: "Leap toward your aim. Untouchable until you land (1 second)." },
};

// lyt: how its Lyt Attack lands — a lunge, or a shot
export const LINES = {
  spectral_tiger: {
    name: "Spectral Tiger", lord: "S'aolesh", kin: "Beast", ilk: "Phantom", lyt: "lunge", lytName: "Pounce",
    tiers: { beb: { title: "Spectral Tiger Beb", lyf: 2, lyt: 6 }, champion: { title: "Spectral Tiger Champion", lyf: 3, lyt: 6 }, lord: { title: "Spectral Tiger Lord", lyf: 2, lyt: 8 } },
    impera: ["spectral_paws", "spectral_claws", "haunting_presents"], uhlti: "haunting_presence",
  },
  shadow_fox: {
    name: "Shadow Fox", lord: "Raeyn'ao", kin: "Beast", ilk: "Phantom", lyt: "lunge", lytName: "Shadow Nip",
    tiers: { beb: { title: "Shadow Fox Beb", lyf: 6, lyt: 2 }, champion: { title: "Shadow Fox Champion", lyf: 6, lyt: 3 }, lord: { title: "Shadow Fox Lord", lyf: 6, lyt: 4 } },
    impera: ["hidden_paws", "shadow_fur", "lord_of_shadows"], uhlti: "royal_darkness",
  },
  inverted_serpent: {
    name: "Inverted Serpent", lord: "Ouraoth", kin: "Beast", ilk: "Phantom", lyt: "lunge", lytName: "Coil Strike",
    tiers: { beb: { title: "Inverted Serpent Beb", lyf: 4, lyt: 4 }, champion: { title: "Inverted Serpent Champion", lyf: 4, lyt: 5 }, lord: { title: "Inverted Serpent Lord", lyf: 4, lyt: 6 } },
    impera: ["phantom_fangs", "supernatural_bite", "inverted_scales"], uhlti: "inverted_fangs",
  },
  chaos_dragon: {
    name: "Chaos Dragon", lord: "Xerath'oun", kin: "Dragon", ilk: "Dragon", lyt: "lunge", lytName: "Ravage",
    tiers: { beb: { title: "Chaos Dragon Beb", lyf: 0, lyt: 8 }, champion: { title: "Chaos Dragon Champion", lyf: 0, lyt: 9 }, lord: { title: "Chaos Dragon Lord", lyf: 0, lyt: 10 } },
    impera: ["natural_disaster", "thriving_in_chaos", "none_more_than_i"], uhlti: "i_or_none",
  },
  lucky_dragon: {
    name: "Lucky Dragon", lord: "V'yornhal", kin: "Dragon", ilk: "Dragon", lyt: "lunge", lytName: "Shade Claw",
    tiers: { beb: { title: "Lucky Dragon Beb", lyf: 1, lyt: 7 }, champion: { title: "Lucky Dragon Champion", lyf: 3, lyt: 6 }, lord: { title: "Lucky Dragon Lord", lyf: 3, lyt: 7 } },
    impera: ["pay_day", "black_cats_best_friend", "fate_maker"], uhlti: "dragon_eyes",
  },
  premier_dragon: {
    name: "Premier Dragon", lord: "Aeodrith", kin: "Dragon", ilk: "Dragon", lyt: "lunge", lytName: "Bloom Bite",
    tiers: { beb: { title: "Premier Dragon Beb", lyf: 4, lyt: 4 }, champion: { title: "Premier Dragon Champion", lyf: 5, lyt: 4 }, lord: { title: "Premier Dragon Lord", lyf: 5, lyt: 5 } },
    impera: ["good_morning", "social_dragonfly", "destined_for_greatness"], uhlti: "show_stomper",
  },
  z4z: {
    name: "Z4Z", lord: "Quan-7", kin: "Machine", ilk: "Time", lyt: "lunge", lytName: "Gear Strike",
    tiers: { beb: { title: "Z4Z Demon", lyf: 0, lyt: 8 }, champion: { title: "Z4Z Wizard", lyf: 2, lyt: 7 }, lord: { title: "Z4Z H'ehll's Sword", lyf: 2, lyt: 8 } },
    impera: ["relentless", "catastrophe", "apocalypse"], uhlti: "apocalypse_now",
  },
  antdroid: {
    name: "Antdroid", lord: "Roztal", kin: "Machine", ilk: "Time", lyt: "lunge", lytName: "Mandible",
    tiers: { beb: { title: "Antdroid Beb", lyf: 5, lyt: 3 }, champion: { title: "Antdroid Champion", lyf: 6, lyt: 3 }, lord: { title: "Antdroid Lord", lyf: 7, lyt: 3 } },
    impera: ["restless", "equivalent_exchange", "king_of_the_anthill"], uhlti: "king_of_the_hill",
  },
  dg0: {
    name: "DG-0", lord: "D'aeogoz", kin: "Machine", ilk: "Time", lyt: "shot", lytName: "Halo Bolt",
    tiers: { beb: { title: "DG-0 Angel", lyf: 6, lyt: 2 }, champion: { title: "DG-0 Farmer", lyf: 7, lyt: 2 }, lord: { title: "DG-0 I'nfini's Shield", lyf: 8, lyt: 2 } },
    impera: ["hope", "law_and_order", "justice_prevails"], uhlti: "justice_reigns",
  },
  h01: {
    name: "H-01", lord: "Calvarus", kin: "Humanoid", ilk: "Astraea", lyt: "shot", lytName: "Blue Blaster",
    tiers: { beb: { title: "H-01 Blue Giant", lyf: 6, lyt: 2 }, champion: { title: "H-01 Quasar", lyf: 7, lyt: 2 }, lord: { title: "H-01 Super Nova", lyf: 1, lyt: 9 } },
    impera: ["natural_glow", "natural_lyt", "morning_star"], uhlti: "super_nova",
  },
  h09: {
    name: "H-09", lord: "Marylynn", kin: "Humanoid", ilk: "Astraea", lyt: "shot", lytName: "Star Blaster",
    tiers: { beb: { title: "H-09 Neutron Star", lyf: 3, lyt: 5 }, champion: { title: "H-09 Pulsar", lyf: 4, lyt: 5 }, lord: { title: "H-09 Hyper Nova", lyf: 0, lyt: 10 } },
    impera: ["cosmic_casbah", "binary_star", "radiant_anima"], uhlti: "radiant_shower",
  },
  xulan: {
    name: "Xulan", lord: "Os'orun-Veth", kin: "Humanoid", ilk: "Astraea", lyt: "lunge", lytName: "Void Lash",
    tiers: { beb: { title: "Xulan Wanderer", lyf: 2, lyt: 6 }, champion: { title: "Surgeon", lyf: 2, lyt: 7 }, lord: { title: "Xulan Shadow", lyf: 0, lyt: 10 } },
    impera: ["diamond_skin", "natural_scalpel", "eternal_nightmare"], uhlti: "sweet_dreams",
  },
};

export const LINE_IDS = Object.keys(LINES);

export const cardUrl = (line, tier) => `${process.env.PUBLIC_URL || ""}/cards/${line}_${tier}.webp`;
export const portraitUrl = (line, tier) => `${process.env.PUBLIC_URL || ""}/cards/${line}_${tier}_art.webp`;

export function tierStats(line, tier) {
  const t = LINES[line].tiers[tier];
  return { ...t, anima: ANIMA_BY_TIER[tier] };
}

// Impera I opens at Beb, II at Champion, III at Lord; the U'hlti is the Lord's.
export const IMPERA_UNLOCK = ["beb", "champion", "lord"];
export const imperaOpen = (i, tier) => TIER_ORDER.indexOf(tier) >= TIER_ORDER.indexOf(IMPERA_UNLOCK[i]);
